import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {serverAPI} from 'features/server/api/serverAPI';
import {
    SERVER_METRICS_CPU,
    SERVER_METRICS_LA,
    SERVER_METRICS_MEM_QUERY,
    SERVER_METRICS_POLLING_INTERVAL,
    ServerMetricsRangeKey,
} from 'features/server/lib/serverMetrics';
import {
    formatServerMetricsTick,
    ServerMetricsChartRow,
    toChartRows,
    toFsChartRows,
    toMemoryUsageChartRows,
} from 'features/server/lib/serverMetricsHelper';
import {
    getServerMetricsGranularity,
    getServerMetricsRangeOptions,
    getServerMetricsSeries,
    getServerMetricsSummary,
    IServerMetricsRange,
    IServerMetricsRangeOption,
    IServerMetricsSeries,
    IServerMetricsSummary,
} from 'features/server/lib/serverMetricsView';
import {userSelector} from 'entities/user';
import {getFormattedDate} from 'shared/lib/datetime';

import IServerOutput from './Server/IServer';
import {IServerMount} from './Server/IServerMetrics';
import useServerMetricsRange from './useServerMetricsRange';

export interface IUseServerMetrics {
    tzString: string | null | undefined,
    range: IServerMetricsRange,
    activePreset: ServerMetricsRangeKey | null,
    rangeOptions: IServerMetricsRangeOption[],
    handlePresetClick: (_key: ServerMetricsRangeKey) => void,
    handleCustomRangeChange: (_from: number, _to: number) => void,
    handleRefresh: () => void,
    isFetching: boolean,
    isLoading: boolean,
    mounts: IServerMount[] | undefined,
    summary: IServerMetricsSummary | null,
    series: IServerMetricsSeries,
    cpuData: ServerMetricsChartRow[],
    laData: ServerMetricsChartRow[],
    memData: ServerMetricsChartRow[],
    fsDataByMount: Record<string, ServerMetricsChartRow[]>,
    tickFormatter: (_ts: number) => string,
    tooltipLabelFormatter: (_ts: number) => string,
}

export const useServerMetrics = (server: IServerOutput): IUseServerMetrics => {
    const dispatch = useDispatch();
    const user = useSelector(userSelector);
    const tzString = user?.timezone;

    const {range, activePreset, syncNow, handlePresetClick, handleCustomRangeChange} = useServerMetricsRange();

    const rangeOptions = useMemo(() => getServerMetricsRangeOptions(server.retention_days), [server.retention_days]);

    const {
        data: mounts,
        isSuccess: isMountsLoaded,
        isError: isMountsFailed,
    } = serverAPI.useGetServerMountsQuery(server.uuid);
    const isMountsSettled = isMountsLoaded || isMountsFailed;

    const {data: metrics, isFetching, refetch} = serverAPI.useGetServerMetricsQuery({
        uuid: server.uuid,
        metrics: [...SERVER_METRICS_CPU, ...SERVER_METRICS_LA, ...SERVER_METRICS_MEM_QUERY],
        mounts: mounts?.map((item) => item.mount) ?? [],
        from: range.from,
        to: range.to,
    }, {
        skip: !isMountsSettled,
        pollingInterval: SERVER_METRICS_POLLING_INTERVAL,
    });

    const isLoading = !isMountsSettled || (isFetching && !metrics);

    const handleRefresh = useCallback(() => {
        syncNow();
        dispatch(serverAPI.util.invalidateTags([{type: 'Server' as const, id: server.uuid}]));

        if (isMountsSettled) {
            refetch();
        }
    }, [dispatch, isMountsSettled, refetch, server.uuid, syncNow]);

    const granularity = getServerMetricsGranularity(range);
    const tickFormatter = useCallback(
        (ts: number) => formatServerMetricsTick(ts, granularity, tzString),
        [granularity, tzString],
    );
    const tooltipLabelFormatter = useCallback(
        (ts: number) => getFormattedDate(new Date(ts * 1000), {tzString}),
        [tzString],
    );

    const cpuData = useMemo(() => toChartRows(metrics, SERVER_METRICS_CPU), [metrics]);
    const laData = useMemo(() => toChartRows(metrics, SERVER_METRICS_LA), [metrics]);
    const memData = useMemo(() => toMemoryUsageChartRows(metrics), [metrics]);
    const fsDataByMount = useMemo(() => Object.fromEntries(
        (mounts ?? []).map((item) => [item.mount, toFsChartRows(metrics, item.mount)]),
    ), [metrics, mounts]);

    const series = useMemo(() => getServerMetricsSeries(), []);
    const summary = server.last_metrics ? getServerMetricsSummary(server.last_metrics, server) : null;

    return {
        tzString,
        range,
        activePreset,
        rangeOptions,
        handlePresetClick,
        handleCustomRangeChange,
        handleRefresh,
        isFetching,
        isLoading,
        mounts,
        summary,
        series,
        cpuData,
        laData,
        memData,
        fsDataByMount,
        tickFormatter,
        tooltipLabelFormatter,
    };
};

export default useServerMetrics;
